/* PulsePill — Dynamic-Island-style live activity pill.
 *
 * Sits in the top nav next to the hamburger on mobile (and desktop too).
 * Shows "● PULSE · LIVE · N new" with a pulsing dot. Tapping it slides
 * an inline drawer down UNDER the navbar (never a new page). The drawer
 * lists the latest events + a "View All →" link to /pulse.
 *
 * Data source: existing /api/pulse/live endpoint. Auto-refreshes every
 * 30s so the count stays honest.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ShieldCheck, BadgeCheck, Sparkles, Newspaper, Film, Video,
  Pencil, Link as LinkIcon, Flag, Trophy, Award, Handshake, Radio, Rocket,
  Megaphone, Flame, UserPlus, ListChecks, ArrowRightLeft, Users as UsersIcon,
  Activity, X,
} from 'lucide-react';
import { timeAgo, eventHref } from './pulse/ThePulseStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  'shield-check': ShieldCheck, 'badge-check': BadgeCheck, 'sparkles': Sparkles,
  'newspaper': Newspaper, 'film': Film, 'video': Video, 'pencil': Pencil,
  'link': LinkIcon, 'flag': Flag, 'arrow-right-left': ArrowRightLeft,
  'trophy': Trophy, 'award': Award, 'handshake': Handshake, 'radio': Radio,
  'users-plus': UserPlus, 'user-plus': UserPlus, 'list': ListChecks,
  'rocket': Rocket, 'megaphone': Megaphone, 'flame': Flame,
  'activity': Activity, 'users': UsersIcon,
};

const TONE_COLOR = {
  blue: '#60a5fa', emerald: '#34d399', green: '#4ade80', amber: '#fbbf24',
  slate: '#94a3b8', purple: '#a78bfa', orange: '#fb923c', cyan: '#22d3ee',
  rose: '#fb7185',
};

/** Track how many events the user has "seen" so the "N new" badge only
 *  reflects fresh activity — mirrors BottomNav's saved-items counter. */
const seenKey = 'flyt_pulse_seen_iso';

const PulsePill = ({ isDark = true, solid = false }) => {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => {
    try { return localStorage.getItem(seenKey) || new Date(Date.now() - 24 * 3600e3).toISOString(); }
    catch { return new Date(Date.now() - 24 * 3600e3).toISOString(); }
  });
  // iter236 · Snapshot lastSeen when the drawer opens so newly-fresh items
  // stay highlighted for the duration of THIS view — even though we
  // persist a newer lastSeen so next time round they're greyed out.
  const [openSnapshot, setOpenSnapshot] = useState(lastSeen);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pulse/live?limit=12`, { withCredentials: true });
      setEvents(data?.events || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  // Click-outside to close the drawer.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  const newCount = events.filter((e) => e.created_at > lastSeen).length;

  const markSeen = () => {
    const now = new Date().toISOString();
    setLastSeen(now);
    try { localStorage.setItem(seenKey, now); } catch { /* ignore */ }
  };

  const toggle = () => {
    // iter484 · Light haptic when the athlete taps the Pulse pill so
    // it feels tactile every time they check "who's on the network
    // right now" — a signature moment of the app.
    try {
      import('../lib/nativeBridge').then((m) => m.haptic('light')).catch(() => {});
    } catch { /* noop */ }
    if (!open) {
      // Freeze the current lastSeen so the drawer highlights fresh
      // items while it's open, then persist a newer lastSeen so the
      // next open shows those items as already-seen.
      setOpenSnapshot(lastSeen);
      markSeen();
    }
    setOpen((v) => !v);
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        className={`inline-flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full ring-1 transition ${
          solid
            ? 'ring-black/0 bg-black hover:brightness-110 text-white'
            : isDark
              ? 'ring-white/15 bg-white/[0.06] hover:bg-white/[0.12] text-white'
              : 'ring-black/10 bg-black/[0.04] hover:bg-black/[0.08] text-black'
        }`}
        aria-expanded={open}
        aria-label={`The Pulse — ${newCount} new`}
        data-testid="pulse-pill"
      >
        <span className="relative inline-flex w-2 h-2">
          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
          <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-400" />
        </span>
        <span className="font-display text-[10px] uppercase tracking-[0.22em] font-black">Pulse</span>
        <span className={`text-[10px] uppercase tracking-widest font-bold ${solid || isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
          Live{newCount > 0 ? ` · ${Math.min(99, newCount)} new` : ''}
        </span>
      </button>

      {/* Slide-down panel. Absolutely-positioned INSIDE the navbar wrapper
          so it drops immediately under the pill, iMessage-style. */}
      {open && (
        <div
          className="fixed sm:absolute left-2 sm:left-auto right-2 sm:right-0 top-[68px] sm:top-[52px] w-[calc(100vw-16px)] sm:w-[380px] max-w-[420px] rounded-2xl bg-neutral-950 ring-1 ring-white/15 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.8)] z-[240] overflow-hidden animate-[pulse-slide_180ms_ease-out]"
          data-testid="pulse-pill-panel"
          style={{ transformOrigin: 'top right' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-400" />
              </span>
              <p className="font-display text-xs uppercase tracking-[0.28em] text-white font-bold">The Pulse</p>
              <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Live</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-full hover:bg-white/10 text-white/60 hover:text-white inline-flex items-center justify-center"
              aria-label="Close"
              data-testid="pulse-pill-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Event list */}
          <ul className="max-h-[62vh] overflow-y-auto divide-y divide-white/[0.06]" data-testid="pulse-pill-list">
            {events.length === 0 && (
              <li className="px-4 py-10 text-center text-white/40 text-sm">
                Quiet for the moment.
              </li>
            )}
            {events.map((ev) => {
              const Icon = ICON_MAP[ev.icon] || Activity;
              const dot = TONE_COLOR[ev.tone] || TONE_COLOR.slate;
              // iter236 · Fresh items get a live green pulse dot; already-
              // seen items fade to a grey static dot with dimmed text so
              // the eye lands on what's actually new at a glance.
              const isNew = (ev.created_at || '') > openSnapshot;
              return (
                <li key={ev.id}>
                  <Link
                    to={eventHref(ev)}
                    onClick={() => setOpen(false)}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-white/[0.04] transition ${
                      isNew ? '' : 'opacity-55'
                    }`}
                    data-testid={`pulse-pill-event-${ev.id}`}
                  >
                    {isNew ? (
                      <span className="mt-1 relative inline-flex w-2 h-2 flex-shrink-0" aria-label="new">
                        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                        <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-400" />
                      </span>
                    ) : (
                      <span
                        className="mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: '#4b5563' }}
                        aria-label="seen"
                      />
                    )}
                    <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isNew ? 'text-white/80' : 'text-white/45'}`} style={isNew ? { color: dot } : undefined} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-[13px] leading-snug ${isNew ? 'text-white font-semibold' : 'text-white/70'}`}>
                        {ev.headline}
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold mt-0.5">
                        {(ev.event_type || '').replace(/_/g, ' ')} · {timeAgo(ev.created_at)}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Footer — View all + See more shortcut */}
          <div className="border-t border-white/10 flex items-center justify-between px-4 py-3">
            <Link
              to="/pulse"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-black text-white hover:text-blue-200 transition"
              data-testid="pulse-pill-view-all"
            >
              View all <ArrowRight className="w-3 h-3" />
            </Link>
            <Link
              to="/pulse"
              onClick={() => setOpen(false)}
              className="text-[11px] uppercase tracking-widest font-bold text-white/50 hover:text-white transition"
              data-testid="pulse-pill-see-more"
            >
              See more
            </Link>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse-slide {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );
};

export default PulsePill;
